import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { AuthService } from 'src/app/auth/auth.service';
import { SessionService } from 'src/app/auth/session.service';
import { AppGlobalControlValues } from 'src/app/common/app.global.control.values';
import { promiseWithCatch } from 'src/app/common/app.helpers';
import AppSettings from 'src/app/common/app.settings';
import ProjectLinks from 'src/app/common/project.links';
import { AccountStatus, UserRole } from 'src/app/swagger-api';
import { FileCategories, FileOwnershipTypes } from '../../files/files-manager.enum';

@Component({
  selector: 'app-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss'],
})
export class HeaderComponent implements OnInit, OnDestroy {
  public AppSettings: typeof AppSettings = AppSettings;

  public readonly LOG_OUT_BUTTON = AppGlobalControlValues.LOG_OUT_BUTTON;
  public readonly ACTIVE = AccountStatus.ACTIVE;

  public isAuthenticated = false;
  public is2AuthLevelPassed = false;
  public isAdminSession = false;

  public userIsDoctor = false;
  public userIsPatient = false;
  public avatarLink = '';

  public isNavbarCollapsed = true;

  private sessionSubscription$: Subscription;

  private sessionService = inject(SessionService);
  private authService = inject(AuthService);
  private router = inject(Router);

  public constructor() {
    this.isAdminSession = this.sessionService.isAdminSession();
  }

  public ngOnInit(): void {
    this.subscribesOnChanges();
  }

  public changeNavbarStatus(): void {
    this.isNavbarCollapsed = !this.isNavbarCollapsed;
  }

  public ngOnDestroy(): void {
    if (this.sessionSubscription$) {
      this.sessionSubscription$.unsubscribe();
    }
  }

  public get loginLink(): string {
    return ProjectLinks.LINK_LOGIN;
  }

  public get registrationLink(): string {
    return ProjectLinks.LINK_REGISTRATION;
  }

  public get projectsLink(): string {
    return ProjectLinks.LINK_PROJECTS;
  }

  public get profileLink(): string {
    return ProjectLinks.LINK_PROFILE_VIEW;
  }

  public get charityFocusesLink(): string {
    return ProjectLinks.LINK_CHARITY_FOCUSES;
  }

  public secondAuthLevelNav(linkPage: string): void {
    promiseWithCatch(
      this.is2AuthLevelPassed
        ? this.router.navigateByUrl(linkPage)
        : this.router.navigate([ProjectLinks.LINK_SECOND_AUTH_LEVEL], {
            state: { linkPage },
          }),
    );
  }

  public get communitiesLink(): string {
    return ProjectLinks.LINK_COMMUNITY_ALL;
  }

  public get postsLink(): string {
    return ProjectLinks.LINK_POSTS;
  }

  public get connectionsLink(): string {
    return ProjectLinks.LINK_CONNECTIONS;
  }

  public get deactivatedUserLink(): string {
    return ProjectLinks.LINK_DEACTIVATED_USERS;
  }

  public get diseasesAndSpecialtiesLink(): string {
    return ProjectLinks.LINK_CATEGORIES;
  }

  public get adminsLink(): string {
    return ProjectLinks.LINK_ADMINS;
  }

  public get doctorLinks(): string {
    return ProjectLinks.LINK_DOCTOR;
  }

  public get patientLinks(): string {
    return ProjectLinks.LINK_PATIENT;
  }

  public get chatLink(): string {
    return ProjectLinks.LINK_CHAT;
  }

  public get filesManagerLink(): string {
    return `${ProjectLinks.LINK_FILES}/${FileOwnershipTypes.manager}?type=${Object.keys(FileCategories)[0]}`;
  }

  public get notificationPreferencesLink(): string {
    return ProjectLinks.LINK_PREFERENCES;
  }

  public get defaultLink(): string {
    return ProjectLinks.LINK_DEFAULT;
  }

  public logout(): void {
    this.authService.serverSideLogout();
    this.authService.clientSideLogout();
  }

  private subscribesOnChanges(): void {
    this.sessionSubscription$ = this.sessionService.sessionChanges.subscribe(() => {
      this.setAuthentificatedFlag();
    });

    this.setAuthentificatedFlag();
  }

  private setAuthentificatedFlag(): void {
    this.isAuthenticated = this.sessionService.isSessionActive();
    this.is2AuthLevelPassed = this.sessionService.is2AuthLvlPassed;
    if (this.isAuthenticated && this.sessionService.user) {
      this.isAdminSession = this.sessionService.isAdminSession();
      this.avatarLink = this.sessionService.user.image_url;
      this.userIsDoctor = this.sessionService.user.user_roles.includes(UserRole.Doctor);
      this.userIsPatient = this.sessionService.user.user_roles.includes(UserRole.Patient);
    } else {
      this.avatarLink = '';
      this.isAdminSession = false;
    }
  }
}
